import { useEffect, useState } from "react";
import { useApi } from "../auth/AuthProvider";

export interface QueryState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/** Minimal `useQuery` wired to the authenticated fetcher. We intentionally
 *  don't pull in @tanstack/react-query yet — the cloud UI is polling-based
 *  and small, and a hand-rolled fetcher keeps the surface area tight.
 *
 *  Pass `opts.refetchIntervalMs` to enable periodic background refetch
 *  while the endpoint is non-null. Refetch fires only when the document is
 *  visible so a tab in the background doesn't burn requests. */
export function useQuery<T>(
  endpoint: string | null,
  opts: { refetchIntervalMs?: number } = {},
): QueryState<T> & { lastFetchedAt: number | null } {
  const api = useApi();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  // `loadedEndpoint` records which endpoint the latest finished fetch was for.
  // On the first render where `endpoint` becomes non-null, loadedEndpoint is
  // still null — so `loading` is derived as true *synchronously* in render,
  // before the effect runs. Without this, route guards that gate on
  // `loading === false && data === null` would briefly see a false "no data"
  // and bail to a fallback URL on hard reloads (the venue-guard bug).
  const [loadedEndpoint, setLoadedEndpoint] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  const [tick, setTick] = useState(0);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!endpoint) {
      setData(null);
      setError(null);
      setLoadedEndpoint(null);
      setFetching(false);
      return;
    }
    let cancelled = false;
    setFetching(true);
    setError(null);
    void api(endpoint)
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !json.success) {
          setError(json.error ?? `HTTP ${res.status}`);
          return;
        }
        setData((json.data as T) ?? null);
        setLastFetchedAt(Date.now());
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setFetching(false);
        setLoadedEndpoint(endpoint);
      });
    return () => {
      cancelled = true;
    };
  }, [api, endpoint, tick]);

  useEffect(() => {
    if (!endpoint || !opts.refetchIntervalMs) return;
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      setTick((t) => t + 1);
    }, opts.refetchIntervalMs);
    return () => clearInterval(id);
  }, [endpoint, opts.refetchIntervalMs]);

  // Loading whenever there's a target endpoint that the most recent successful
  // fetch hasn't yet caught up to, OR a fetch is currently in flight (e.g.
  // refetch on the same endpoint).
  const loading = (endpoint !== null && loadedEndpoint !== endpoint) || fetching;
  // Only surface data once it corresponds to the current endpoint — otherwise
  // a component briefly sees the previous endpoint's data after the URL flips.
  const effectiveData = loadedEndpoint === endpoint ? data : null;

  return { data: effectiveData, loading, error, refetch: () => setTick((t) => t + 1), lastFetchedAt };
}
